import { SERVICES_API_BASE_URL } from './apiConfig';

interface ApiErrorPayload {
  message?: string;
  code?: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
  }
}

function getRequestHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function getErrorPayload(response: Response): Promise<ApiErrorPayload> {
  try {
    return await response.json() as ApiErrorPayload;
  } catch {
    return {};
  }
}

export async function apiFetch<TResponse>(
  path: string,
  options: RequestInit = {},
): Promise<TResponse> {
  const response = await fetch(`${SERVICES_API_BASE_URL}${path}`, {
    ...options,
    headers: { ...getRequestHeaders(), ...options.headers },
  });

  if (!response.ok) {
    const error = await getErrorPayload(response);

    if (response.status === 401) {
      window.dispatchEvent(new Event('strivre:unauthorized'));
    }

    if (response.status === 403 && error.code === 'PASSWORD_EXPIRED') {
      window.dispatchEvent(
        new Event('strivre:password-expired')
      );
    }

    throw new ApiError(error.message ?? response.statusText, response.status, error.code);
  }

  if (response.status === 204) {
    return undefined as TResponse;
  }

  return response.json() as Promise<TResponse>;
}

export function postJson<TResponse>(path: string, body?: unknown): Promise<TResponse> {
  return apiFetch<TResponse>(path, {
    method: 'POST',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export const getApiErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof ApiError) return error.message || fallback;
  return error instanceof Error ? error.message : fallback;
};